import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import { LanguageLayout } from "./components/LanguageLayout";
import { LanguageRedirect } from "./components/LanguageRedirect";
import { CookieBanner } from "./components/CookieBanner";

// Eagerly loaded - needed for initial render
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy loaded - not needed on initial page load
const Auth = lazy(() => import("./pages/Auth"));
const EStyling = lazy(() => import("./pages/EStyling"));
const ProjectLayout = lazy(() => import("./pages/ProjectLayout"));
const ImageDetail = lazy(() => import("./pages/ImageDetail"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminPrompter = lazy(() => import("./pages/AdminPrompter"));
const AdminJobs = lazy(() => import("./pages/AdminJobs"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminImages = lazy(() => import("./pages/AdminImages"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const Profile = lazy(() => import("./pages/Profile"));
const Payments = lazy(() => import("./pages/Payments"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Help = lazy(() => import("./pages/Help"));
const Examples = lazy(() => import("./pages/Examples"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const WhatIsEstyling = lazy(() => import("./pages/WhatIsEstyling"));
const EstylingComparison = lazy(() => import("./pages/EstylingComparison"));
const EstylingPricing = lazy(() => import("./pages/EstylingPricing"));
const ShareProject = lazy(() => import("./pages/ShareProject"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminContact = lazy(() => import("./pages/AdminContact"));
const AdminComposite = lazy(() => import("./pages/AdminComposite"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

// Routes that should be available under each language
const LocalizedRoutes = () => (
  <>
    <Route index element={<Index />} />
    <Route path="auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
    <Route path="forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
    <Route path="reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
    <Route path="confirm-email" element={<Suspense fallback={<PageLoader />}><ConfirmEmail /></Suspense>} />
    <Route path="profile" element={<Suspense fallback={<PageLoader />}><Profile /></Suspense>} />
    <Route path="payments" element={<Suspense fallback={<PageLoader />}><Payments /></Suspense>} />
    <Route path="payments/success" element={<Suspense fallback={<PageLoader />}><PaymentSuccess /></Suspense>} />
    <Route path="payments/cancel" element={<Suspense fallback={<PageLoader />}><PaymentCancel /></Suspense>} />
    <Route path="privacy" element={<Suspense fallback={<PageLoader />}><Privacy /></Suspense>} />
    <Route path="help" element={<Suspense fallback={<PageLoader />}><Help /></Suspense>} />
    <Route path="examples" element={<Suspense fallback={<PageLoader />}><Examples /></Suspense>} />
    <Route path="faq" element={<Suspense fallback={<PageLoader />}><FAQ /></Suspense>} />
    <Route path="about" element={<Suspense fallback={<PageLoader />}><About /></Suspense>} />
    <Route path="contact" element={<Suspense fallback={<PageLoader />}><Contact /></Suspense>} />
    <Route path="kontakt" element={<Suspense fallback={<PageLoader />}><Contact /></Suspense>} />
    <Route path="estyling" element={<Suspense fallback={<PageLoader />}><EStyling /></Suspense>} />
    <Route path="vad-ar-e-styling" element={<Suspense fallback={<PageLoader />}><WhatIsEstyling /></Suspense>} />
    <Route path="what-is-estyling" element={<Suspense fallback={<PageLoader />}><WhatIsEstyling /></Suspense>} />
    <Route path="digital-vs-traditionell-styling" element={<Suspense fallback={<PageLoader />}><EstylingComparison /></Suspense>} />
    <Route path="estyling-comparison" element={<Suspense fallback={<PageLoader />}><EstylingComparison /></Suspense>} />
    <Route path="priser-e-styling" element={<Suspense fallback={<PageLoader />}><EstylingPricing /></Suspense>} />
    <Route path="estyling-pricing" element={<Suspense fallback={<PageLoader />}><EstylingPricing /></Suspense>} />
    <Route path="project/:projectId" element={<Suspense fallback={<PageLoader />}><ProjectLayout /></Suspense>}>
      <Route path="image/:imageId" element={<Suspense fallback={<PageLoader />}><ImageDetail /></Suspense>} />
    </Route>
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <CookieBanner />
        <BrowserRouter>
          <Routes>
            {/* Root redirects to default language */}
            <Route path="/" element={<LanguageRedirect />} />
            
            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageLayout />}>
              {LocalizedRoutes()}
            </Route>

            {/* Public share route (not localized, no auth) */}
            <Route path="/share/:token" element={<Suspense fallback={<PageLoader />}><ShareProject /></Suspense>} />

            {/* Admin routes (not localized) */}
            <Route path="/admin" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><Admin /></Suspense></ProtectedAdminRoute>} />
            <Route path="/admin/prompter" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminPrompter /></Suspense></ProtectedAdminRoute>} />
            <Route path="/admin/jobs" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminJobs /></Suspense></ProtectedAdminRoute>} />
            <Route path="/admin/users" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminUsers /></Suspense></ProtectedAdminRoute>} />
            <Route path="/admin/images" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminImages /></Suspense></ProtectedAdminRoute>} />
            <Route path="/admin/contact" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminContact /></Suspense></ProtectedAdminRoute>} />
            <Route path="/admin/composite" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminComposite /></Suspense></ProtectedAdminRoute>} />

            {/* Legacy routes - redirect to localized versions */}
            <Route path="/auth" element={<LanguageRedirect to="/auth" />} />
            <Route path="/forgot-password" element={<LanguageRedirect to="/forgot-password" />} />
            <Route path="/reset-password" element={<LanguageRedirect to="/reset-password" />} />
            <Route path="/confirm-email" element={<LanguageRedirect to="/confirm-email" />} />
            <Route path="/profile" element={<LanguageRedirect to="/profile" />} />
            <Route path="/payments" element={<LanguageRedirect to="/payments" />} />
            <Route path="/payments/success" element={<LanguageRedirect to="/payments/success" />} />
            <Route path="/payments/cancel" element={<LanguageRedirect to="/payments/cancel" />} />
            <Route path="/privacy" element={<LanguageRedirect to="/privacy" />} />
            <Route path="/help" element={<LanguageRedirect to="/help" />} />
            <Route path="/examples" element={<LanguageRedirect to="/examples" />} />
            <Route path="/faq" element={<LanguageRedirect to="/faq" />} />
            <Route path="/about" element={<LanguageRedirect to="/about" />} />
            <Route path="/contact" element={<LanguageRedirect to="/contact" />} />
            <Route path="/kontakt" element={<LanguageRedirect to="/kontakt" />} />
            <Route path="/estyling" element={<LanguageRedirect to="/estyling" />} />
            <Route path="/vad-ar-e-styling" element={<LanguageRedirect to="/vad-ar-e-styling" />} />
            <Route path="/what-is-estyling" element={<LanguageRedirect to="/what-is-estyling" />} />
            <Route path="/digital-vs-traditionell-styling" element={<LanguageRedirect to="/digital-vs-traditionell-styling" />} />
            <Route path="/estyling-comparison" element={<LanguageRedirect to="/estyling-comparison" />} />
            <Route path="/priser-e-styling" element={<LanguageRedirect to="/priser-e-styling" />} />
            <Route path="/estyling-pricing" element={<LanguageRedirect to="/estyling-pricing" />} />
            <Route path="/project/:projectId/*" element={<LanguageRedirect to="/project" />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
